import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Checkbox } from '../components/ui/checkbox';
import { mockProjects, mockUsers, mockCompanies, mockCategories, mockTasks } from '../mock/data';
import { 
  ChevronDown, ChevronRight, Users, 
  Building2, AlertTriangle, 
  Search, CheckCircle2, AlertCircle, 
  Clock, Pause, XCircle, Filter, Layers, ExternalLink, UserCog, X, Archive,
  ChevronsUpDown, ChevronsDownUp, List, LayoutGrid, Calendar, User
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import HeaderGlow from '../components/HeaderGlow';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

// Calculate project status based on tasks and dates
const calculateProjectStatus = (project, tasks) => {
  if (project.manualStatus) {
    return project.manualStatus;
  }
  
  const today = new Date();
  const endDate = new Date(project.endDate);
  
  if (today > endDate && project.progress < 100) {
    return 'late';
  }
  
  const projectTasks = tasks.filter(t => t.projectId === project.id);
  const hasLateTasks = projectTasks.some(task => {
    const taskDue = new Date(task.dueDate);
    return taskDue < today && task.status !== 'completed';
  });
  
  if (hasLateTasks) {
    return 'late-task';
  }
  
  if (project.progress === 100) {
    return 'completed';
  }
  
  return 'on-time';
};

const getStatusConfig = (status) => {
  switch (status) {
    case 'late':
      return { label: 'Late', color: 'bg-red-500/20 text-red-400', icon: AlertCircle };
    case 'late-task':
      return { label: 'Late Task', color: 'bg-orange-500/20 text-orange-400', icon: AlertTriangle };
    case 'on-time':
      return { label: 'On Time', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 };
    case 'on-hold':
      return { label: 'On Hold', color: 'bg-amber-500/20 text-amber-400', icon: Pause };
    case 'cancelled':
      return { label: 'Cancelled', color: 'bg-slate-500/20 text-slate-400', icon: XCircle };
    case 'completed':
      return { label: 'Completed', color: 'bg-blue-500/20 text-blue-400', icon: CheckCircle2 };
    default:
      return { label: 'Unknown', color: 'bg-slate-500/20 text-slate-400', icon: Clock };
  }
};

// Format date to "dd Mmm YYYY"
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Calculate update status
const getUpdateStatus = (lastUpdateDate) => {
  if (!lastUpdateDate) return 'missed-2w';
  const today = new Date();
  const updateDate = new Date(lastUpdateDate);
  const diffDays = Math.floor((today - updateDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays > 14) return 'missed-2w';
  if (diffDays > 7) return 'missed-1w';
  return 'updated';
};

// Get unique years from projects
const getYearsFromProjects = (projects) => {
  const years = new Set();
  projects.forEach(p => {
    if (p.startDate) years.add(new Date(p.startDate).getFullYear());
    if (p.endDate) years.add(new Date(p.endDate).getFullYear());
  });
  return Array.from(years).sort((a, b) => b - a);
};

const PortfolioManagement = () => {
  // Default: first company expanded, others collapsed
  const [expandedCompanies, setExpandedCompanies] = useState(() => {
    return mockCompanies.length > 0 ? [mockCompanies[0].id] : [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false); // Default hidden
  const { collapsed, setCollapsed } = useSidebar();
  
  // Filter states
  const [yearFilter, setYearFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updateStatusFilter, setUpdateStatusFilter] = useState('all');
  const [escalatedToFilter, setEscalatedToFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('company'); // 'company' or 'none'
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

  // Bulk selection states
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkPMDialogOpen, setBulkPMDialogOpen] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);

  const toggleCompany = (companyId) => {
    setExpandedCompanies(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  // Collapse/Expand all companies
  const toggleAllCompanies = () => {
    const allCompanyIds = projectsDisplay
      .filter(({ company }) => company !== null)
      .map(({ company }) => company.id);
    const allExpanded = allCompanyIds.every(id => expandedCompanies.includes(id));
    if (allExpanded) {
      setExpandedCompanies([]);
    } else {
      setExpandedCompanies(allCompanyIds);
    }
  };

  const areAllExpanded = () => {
    const allCompanyIds = projectsDisplay
      .filter(({ company }) => company !== null)
      .map(({ company }) => company.id);
    return allCompanyIds.length > 0 && allCompanyIds.every(id => expandedCompanies.includes(id));
  };

  // Get available years
  const availableYears = useMemo(() => getYearsFromProjects(mockProjects), []);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return mockProjects.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesYear = yearFilter === 'all' || 
        new Date(p.startDate).getFullYear().toString() === yearFilter ||
        new Date(p.endDate).getFullYear().toString() === yearFilter;
      const matchesCompany = companyFilter === 'all' || p.companyId === companyFilter;
      const matchesPM = pmFilter === 'all' || p.projectManagerId === pmFilter;
      
      const projectStatus = calculateProjectStatus(p, mockTasks);
      const matchesStatus = statusFilter === 'all' || projectStatus === statusFilter;
      
      const updateStatus = getUpdateStatus(p.lastUpdateDate);
      const matchesUpdateStatus = updateStatusFilter === 'all' || updateStatus === updateStatusFilter;
      
      const matchesEscalatedTo = escalatedToFilter === 'all' || 
        (escalatedToFilter === 'none' && !p.isEscalated) ||
        (p.escalatedTo === escalatedToFilter);
      
      return matchesSearch && matchesYear && matchesCompany && matchesPM && 
             matchesStatus && matchesUpdateStatus && matchesEscalatedTo;
    });
  }, [searchQuery, yearFilter, companyFilter, pmFilter, statusFilter, updateStatusFilter, escalatedToFilter]);

  // Group projects by company or flat list
  const projectsDisplay = useMemo(() => {
    if (groupBy === 'none') {
      return [{ company: null, projects: filteredProjects }];
    }
    return mockCompanies.map(company => ({
      company,
      projects: filteredProjects.filter(p => p.companyId === company.id)
    })).filter(group => group.projects.length > 0);
  }, [filteredProjects, groupBy]);

  const handleStatusChange = (projectId, newStatus) => {
    const statusLabel = newStatus === null ? 'Auto (Calculated)' : newStatus;
    alert(`Status for project would be set to: ${statusLabel}\n\nThis will be implemented with backend integration.`);
  };

  // Clear all filters
  const clearFilters = () => {
    setYearFilter('all');
    setCompanyFilter('all');
    setPmFilter('all');
    setStatusFilter('all');
    setUpdateStatusFilter('all');
    setEscalatedToFilter('all');
    setSearchQuery('');
  };

  // Bulk selection handlers
  const toggleProjectSelection = (projectId) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProjects.size === filteredProjects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(filteredProjects.map(p => p.id)));
    }
  };

  const clearSelection = () => {
    setSelectedProjects(new Set());
  };

  const isAllSelected = filteredProjects.length > 0 && selectedProjects.size === filteredProjects.length;

  // Bulk action handlers
  const handleBulkStatusChange = (newStatus) => {
    const projectNames = Array.from(selectedProjects).map(id => 
      mockProjects.find(p => p.id === id)?.name
    ).join(', ');
    
    alert(`Would update status to "${newStatus}" for ${selectedProjects.size} project(s):\n${projectNames}\n\nThis will be implemented with backend integration.`);
    setBulkStatusDialogOpen(false);
    clearSelection();
  };

  const handleBulkPMChange = (newPMId) => {
    const pmName = mockUsers.find(u => u.id === newPMId)?.name;
    const projectNames = Array.from(selectedProjects).map(id => 
      mockProjects.find(p => p.id === id)?.name
    ).join(', ');
    
    alert(`Would reassign PM to "${pmName}" for ${selectedProjects.size} project(s):\n${projectNames}\n\nThis will be implemented with backend integration.`);
    setBulkPMDialogOpen(false);
    clearSelection();
  };

  const handleBulkArchive = () => {
    const projectNames = Array.from(selectedProjects).map(id => 
      mockProjects.find(p => p.id === id)?.name
    ).join(', ');
    
    alert(`Would archive ${selectedProjects.size} project(s):\n${projectNames}\n\nThis will be implemented with backend integration.`);
    setBulkArchiveDialogOpen(false);
    clearSelection();
  };

  const { currentColorTheme } = useTheme();

  // Check if any filter is active
  const hasActiveFilters = yearFilter !== 'all' || companyFilter !== 'all' || 
    pmFilter !== 'all' || statusFilter !== 'all' || 
    updateStatusFilter !== 'all' || escalatedToFilter !== 'all' || searchQuery;

  return (
    <TooltipProvider>
      <div className="flex min-h-screen bg-background">
        {/* Left Filter Panel */}
        {showFilters && (
          <div className="w-64 border-r border-border bg-card/50 p-4 flex-shrink-0 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-muted-foreground">
                  Clear all
                </Button>
              )}
            </div>

            <div className="space-y-4">
              {/* Year Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Year</Label>
                <Select value={yearFilter} onValueChange={setYearFilter}>
                  <SelectTrigger className="h-9" data-testid="year-filter">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Company Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Company</Label>
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="h-9" data-testid="company-filter">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {mockCompanies.map(company => (
                      <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Manager Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Project Manager</Label>
                <Select value={pmFilter} onValueChange={setPmFilter}>
                  <SelectTrigger className="h-9" data-testid="pm-filter">
                    <SelectValue placeholder="All PMs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All PMs</SelectItem>
                    {mockUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-9" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="on-time">On Time</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="late-task">Late Task</SelectItem>
                    <SelectItem value="on-hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Update Status Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Update Status</Label>
                <Select value={updateStatusFilter} onValueChange={setUpdateStatusFilter}>
                  <SelectTrigger className="h-9" data-testid="update-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="updated">Updated</SelectItem>
                    <SelectItem value="missed-1w">Missed 1 Week+</SelectItem>
                    <SelectItem value="missed-2w">Missed 2 Weeks+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Escalated To Filter */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Escalated To</Label>
                <Select value={escalatedToFilter} onValueChange={setEscalatedToFilter}>
                  <SelectTrigger className="h-9" data-testid="escalated-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="none">Not Escalated</SelectItem>
                    {mockUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Group By */}
              <div className="pt-4 border-t border-border">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Group By</Label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="h-9" data-testid="group-by-filter">
                    <Layers className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Group by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="none">Don't Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Header - uses theme colors */}
          <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-purple-500 via-violet-500 to-fuchsia-500'} p-8`}>
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">Portfolio Overview</h1>
                <p className="text-white/80">Monitor and track all projects across your portfolio</p>
              </div>
              <div className="text-right mr-4">
                <p className="text-4xl font-bold text-white">{filteredProjects.length}</p>
                <p className="text-white/80 text-sm">Total Projects</p>
              </div>
            </div>
          </div>
          <HeaderGlow />

          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  const newShowFilters = !showFilters;
                  setShowFilters(newShowFilters);
                  // Collapse sidebar when showing filters
                  if (newShowFilters && !collapsed) {
                    setCollapsed(true);
                  }
                }}
                className={showFilters ? 'bg-primary/10 border-primary' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-[250px] h-9"
                  data-testid="search-projects"
                />
              </div>
              {/* Group by Company Toggle */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setGroupBy(groupBy === 'company' ? 'none' : 'company')}
                className={`flex items-center gap-2 ${groupBy === 'company' ? 'bg-primary/10 border-primary' : ''}`}
                data-testid="toggle-group-by-company-portfolio"
              >
                {groupBy === 'company' ? (
                  <>
                    <Layers className="w-4 h-4" />
                    <span className="hidden sm:inline">Grouped</span>
                  </>
                ) : (
                  <>
                    <List className="w-4 h-4" />
                    <span className="hidden sm:inline">Flat List</span>
                  </>
                )}
              </Button>
              {/* Collapse/Expand All Button - only show when grouped by company */}
              {groupBy === 'company' && viewMode === 'table' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={toggleAllCompanies}
                  className="flex items-center gap-2"
                  data-testid="toggle-all-companies-portfolio"
                >
                  {areAllExpanded() ? (
                    <>
                      <ChevronsDownUp className="w-4 h-4" />
                      <span className="hidden sm:inline">Collapse All</span>
                    </>
                  ) : (
                    <>
                      <ChevronsUpDown className="w-4 h-4" />
                      <span className="hidden sm:inline">Expand All</span>
                    </>
                  )}
                </Button>
              )}
              {/* View Mode Toggle */}
              <div className="flex items-center border border-border rounded-md">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className={`rounded-r-none px-3 ${viewMode === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                  data-testid="view-mode-table"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`rounded-l-none px-3 ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}
                  data-testid="view-mode-grid"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedProjects.size > 0 && (
            <div className="px-4 py-3 border-b border-border bg-primary/10 flex items-center justify-between sticky top-0 z-20" data-testid="bulk-action-toolbar">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-primary/20 text-primary font-semibold">
                    {selectedProjects.size} selected
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={clearSelection} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  {/* Bulk Change Status */}
                  <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="bulk-status-btn">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Change Status
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[400px] bg-card">
                      <DialogHeader>
                        <DialogTitle className="text-foreground">Change Status for {selectedProjects.size} Project(s)</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 pt-4">
                        <Button variant="outline" className="w-full justify-start" onClick={() => handleBulkStatusChange('on-hold')}>
                          <Pause className="w-4 h-4 mr-2 text-amber-500" />
                          Set to On Hold
                        </Button>
                        <Button variant="outline" className="w-full justify-start" onClick={() => handleBulkStatusChange('cancelled')}>
                          <XCircle className="w-4 h-4 mr-2 text-slate-500" />
                          Set to Cancelled
                        </Button>
                        <Button variant="outline" className="w-full justify-start" onClick={() => handleBulkStatusChange('completed')}>
                          <CheckCircle2 className="w-4 h-4 mr-2 text-blue-500" />
                          Set to Completed
                        </Button>
                        <Button variant="outline" className="w-full justify-start" onClick={() => handleBulkStatusChange(null)}>
                          <Clock className="w-4 h-4 mr-2 text-emerald-500" />
                          Reset to Auto (Calculated)
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Bulk Reassign PM */}
                  <Dialog open={bulkPMDialogOpen} onOpenChange={setBulkPMDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="bulk-pm-btn">
                        <UserCog className="w-4 h-4 mr-2" />
                        Reassign PM
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[400px] bg-card">
                      <DialogHeader>
                        <DialogTitle className="text-foreground">Reassign PM for {selectedProjects.size} Project(s)</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3 pt-4">
                        {mockUsers.map(user => (
                          <Button 
                            key={user.id} 
                            variant="outline" 
                            className="w-full justify-start" 
                            onClick={() => handleBulkPMChange(user.id)}
                          >
                            <Avatar className="w-6 h-6 mr-2">
                              <AvatarImage src={user.avatar} />
                              <AvatarFallback className="text-xs">{user.name.charAt(0)}</AvatarFallback>
                            </Avatar>
                            {user.name}
                          </Button>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Bulk Archive */}
                  <Dialog open={bulkArchiveDialogOpen} onOpenChange={setBulkArchiveDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20" data-testid="bulk-archive-btn">
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[400px] bg-card">
                      <DialogHeader>
                        <DialogTitle className="text-foreground">Archive {selectedProjects.size} Project(s)?</DialogTitle>
                      </DialogHeader>
                      <div className="pt-4">
                        <p className="text-muted-foreground mb-4">
                          Are you sure you want to archive the selected projects?
                        </p>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => setBulkArchiveDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button variant="destructive" onClick={handleBulkArchive}>
                            Archive Projects
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          )}

          {/* Table Header - Only show in table view */}
          {viewMode === 'table' && (
            <div className="px-4 py-2 border-b border-border bg-muted/30 sticky top-0 z-10">
              <div className="flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <div className="w-[40px] px-2 flex items-center">
                  <Checkbox 
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    data-testid="select-all-checkbox"
                  />
                </div>
                {groupBy === 'none' && <div className="w-[140px] px-2">Company</div>}
                <div className="flex-1 min-w-[200px] px-2">Project</div>
                <div className="w-[130px] px-2">PM</div>
                <div className="w-[100px] px-2">Status</div>
                <div className="w-[80px] px-2 text-center">Progress</div>
                <div className="flex-1 min-w-[200px] px-2">Update</div>
                <div className="w-[100px] px-2">Due Date</div>
                <div className="w-[80px] px-2 text-center">Escalated</div>
              </div>
            </div>
          )}

          {/* Projects List - Table View */}
          {viewMode === 'table' && (
            <div className="divide-y divide-border">
              {projectsDisplay.map(({ company, projects }) => {
                if (projects.length === 0) return null;
                
                const isGrouped = groupBy === 'company' && company;
                const isExpanded = isGrouped ? expandedCompanies.includes(company.id) : true;

                return (
                  <div key={company?.id || 'all'} data-testid={`company-group-${company?.id || 'all'}`}>
                    {/* Company Header (only when grouped) */}
                    {isGrouped && (
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors bg-card/50"
                        onClick={() => toggleCompany(company.id)}
                      >
                        <div className="flex items-center space-x-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                          <h3 className="font-semibold text-foreground">{company.name}</h3>
                          <Badge variant="secondary" className="text-xs">{projects.length} projects</Badge>
                        </div>
                      </div>
                    )}

                    {/* Projects Rows */}
                    {isExpanded && (
                      <div className="divide-y divide-border/50">
                        {projects.map((project) => {
                          const projectManager = mockUsers.find(u => u.id === project.projectManagerId);
                          const escalatedToUser = project.isEscalated ? mockUsers.find(u => u.id === project.escalatedTo) : null;
                          const status = calculateProjectStatus(project, mockTasks);
                          const statusConfig = getStatusConfig(status);
                          const StatusIcon = statusConfig.icon;
                          const projectCompany = mockCompanies.find(c => c.id === project.companyId);
                          const isSelected = selectedProjects.has(project.id);
                          
                          return (
                            <div
                              key={project.id}
                              className={`flex items-center py-3 px-4 hover:bg-muted/20 transition-colors group ${isSelected ? 'bg-primary/5' : ''}`}
                              data-testid={`project-row-${project.id}`}
                            >
                              {/* Checkbox */}
                              <div className="w-[40px] px-2 flex items-center">
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => toggleProjectSelection(project.id)}
                                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                  data-testid={`checkbox-${project.id}`}
                                />
                              </div>

                              {/* Company Column (only when ungrouped) */}
                              {groupBy === 'none' && (
                                <div className="w-[140px] px-2">
                                  <span className="text-sm text-foreground truncate block">{projectCompany?.name}</span>
                                </div>
                              )}

                              {/* Project Name & Due Date */}
                              <div className="flex-1 min-w-[200px] px-2">
                                <a
                                  href={`/projects/${project.id}/details`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group/link inline-flex items-center gap-1 hover:text-primary transition-colors"
                                  data-testid={`project-link-${project.id}`}
                                >
                                  <span className="font-medium text-foreground group-hover/link:text-primary">{project.name}</span>
                                  <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                                </a>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  Due: {formatDate(project.endDate)}
                                </div>
                              </div>

                              {/* PM */}
                              <div className="w-[130px] px-2">
                                <span className="text-sm text-foreground">{projectManager?.name || '-'}</span>
                              </div>

                              {/* Status */}
                              <div className="w-[100px] px-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`${statusConfig.color} text-xs h-7 px-2 hover:opacity-80`}
                                      data-testid={`status-btn-${project.id}`}
                                    >
                                      <StatusIcon className="w-3 h-3 mr-1" />
                                      {statusConfig.label}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start" className="w-40">
                                    <DropdownMenuItem onClick={() => handleStatusChange(project.id, null)}>
                                      <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                                      Auto (Calculated)
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'on-hold')}>
                                      <Pause className="w-4 h-4 mr-2 text-amber-500" />
                                      On Hold
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'cancelled')}>
                                      <XCircle className="w-4 h-4 mr-2 text-slate-500" />
                                      Cancelled
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusChange(project.id, 'completed')}>
                                      <CheckCircle2 className="w-4 h-4 mr-2 text-blue-500" />
                                      Completed
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              {/* Progress - uses theme gradient */}
                              <div className="w-[80px] px-2">
                                <div className="flex items-center gap-2">
                                  <Progress 
                                    value={project.progress} 
                                    className="h-1.5 flex-1" 
                                    indicatorClassName={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                                  />
                                  <span className="text-xs font-medium text-foreground w-8">{project.progress}%</span>
                                </div>
                              </div>

                              {/* Update */}
                              <div className="flex-1 min-w-[200px] px-2">
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {project.lastUpdate || 'No updates yet'}
                                </p>
                              </div>

                              {/* Due Date */}
                              <div className="w-[100px] px-2">
                                <span className="text-sm text-foreground">{formatDate(project.endDate)}</span>
                              </div>

                              {/* Escalated */}
                              <div className="w-[80px] px-2 text-center">
                                {project.isEscalated ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center justify-center cursor-help">
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      <div className="space-y-1">
                                        <p className="font-medium">Escalated to: {escalatedToUser?.name}</p>
                                        <p className="text-sm text-muted-foreground">{project.escalationReason}</p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Projects Grid View */}
          {viewMode === 'grid' && (
            <div className="p-4">
              {projectsDisplay.map(({ company, projects }) => {
                if (projects.length === 0) return null;
                const isGrouped = groupBy === 'company' && company;

                return (
                  <div key={company?.id || 'all'} className="mb-6" data-testid={`company-grid-${company?.id || 'all'}`}>
                    {/* Company Header (only when grouped) */}
                    {isGrouped && (
                      <div className="flex items-center gap-3 mb-4">
                        <h3 className="font-semibold text-foreground text-lg">{company.name}</h3>
                        <Badge variant="secondary" className="text-xs">{projects.length} projects</Badge>
                      </div>
                    )}

                    {/* Project Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {projects.map((project) => {
                        const projectManager = mockUsers.find(u => u.id === project.projectManagerId);
                        const escalatedToUser = project.isEscalated ? mockUsers.find(u => u.id === project.escalatedTo) : null;
                        const status = calculateProjectStatus(project, mockTasks);
                        const statusConfig = getStatusConfig(status);
                        const StatusIcon = statusConfig.icon;
                        const projectCompany = mockCompanies.find(c => c.id === project.companyId);
                        const isSelected = selectedProjects.has(project.id);

                        return (
                          <Card 
                            key={project.id} 
                            className={`group hover:shadow-lg transition-all duration-200 border-border/50 hover:border-primary/30 ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`}
                            data-testid={`project-card-${project.id}`}
                          >
                            <CardContent className="p-4">
                              {/* Card Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Checkbox 
                                    checked={isSelected}
                                    onCheckedChange={() => toggleProjectSelection(project.id)}
                                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                    data-testid={`checkbox-grid-${project.id}`}
                                  />
                                  <Badge className={`${statusConfig.color} text-xs`}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {statusConfig.label}
                                  </Badge>
                                </div>
                                {project.isEscalated && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help">
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      <div className="space-y-1">
                                        <p className="font-medium">Escalated to: {escalatedToUser?.name}</p>
                                        <p className="text-sm text-muted-foreground">{project.escalationReason}</p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>

                              {/* Project Name */}
                              <a
                                href={`/projects/${project.id}/details`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group/link inline-flex items-center gap-1 hover:text-primary transition-colors mb-2"
                                data-testid={`project-card-link-${project.id}`}
                              >
                                <h4 className="font-semibold text-foreground group-hover/link:text-primary line-clamp-2">
                                  {project.name}
                                </h4>
                                <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity flex-shrink-0" />
                              </a>

                              {/* Company (when ungrouped) */}
                              {groupBy === 'none' && (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                                  <Building2 className="w-3 h-3" />
                                  <span>{projectCompany?.name}</span>
                                </div>
                              )}

                              {/* Progress Bar */}
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">Progress</span>
                                  <span className="font-medium text-foreground">{project.progress}%</span>
                                </div>
                                <Progress 
                                  value={project.progress} 
                                  className="h-2" 
                                  indicatorClassName={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}
                                />
                              </div>

                              {/* Meta Info */}
                              <div className="space-y-1.5 text-xs">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <User className="w-3 h-3" />
                                  <span>{projectManager?.name || 'Unassigned'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Calendar className="w-3 h-3" />
                                  <span>Due: {formatDate(project.endDate)}</span>
                                </div>
                              </div>

                              {/* Last Update */}
                              {project.lastUpdate && (
                                <div className="mt-3 pt-3 border-t border-border/50">
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {project.lastUpdate}
                                  </p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {projectsDisplay.every(g => g.projects.length === 0) && (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No projects found</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters ? 'Try adjusting your filters' : 'No projects in the portfolio yet'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PortfolioManagement;
